import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { FC, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { SimpleButton } from '../../../../common/Buttons';
import { requiredString, submitFormByRef } from '../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../common/Forms/NavigationBlocker';
import { InputField } from '../../../../common/Inputs';
import { FormRow, Row, Visible } from '../../../../common/LayoutComponents';

const testIds = {
  cancelButton: 'OrganisationDetailsForm::cancelButton',
  saveButton: 'OrganisationDetailsForm::saveButton',
  name: 'OrganisationDetailsForm::name',
  phone: 'OrganisationDetailsForm::phone',
  email: 'OrganisationDetailsForm::email',
};

const organisationDetailsFormSchema = z.object({
  id: z.string().optional(),
  name: requiredString,
  privateContactDetails: z.object({
    email: z.string().email().optional().nullable(),
    phone: z.string().optional().nullable(),
  }),
});

export type OrganisationDetailsFormState = z.infer<
  typeof organisationDetailsFormSchema
>;

type OrganisationDetailsFormProps = {
  readonly defaultValues: Partial<OrganisationDetailsFormState>;
  readonly onSubmit: (state: OrganisationDetailsFormState) => void;
  readonly onCancel: () => void;
};

export const OrganisationDetailsForm: FC<OrganisationDetailsFormProps> = ({
  defaultValues,
  onSubmit,
  onCancel,
}) => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const methods = useForm<OrganisationDetailsFormState>({
    defaultValues,
    resolver: zodResolver(organisationDetailsFormSchema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'OrganisationDetailsForm');
  const { handleSubmit } = methods;

  const onSave = () => {
    submitFormByRef(formRef);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          // On the terminal details page, this form gets nested in another form,
          // through a Portal, so fine from HTML perspective, but React still propagates
          // the submit event to top level form by default.
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        ref={formRef}
      >
        <div className="space-y-2">
          <FormRow>
            <InputField<OrganisationDetailsFormState>
              type="text"
              translationPrefix="stopDetails.maintenance.organisation"
              fieldPath="name"
              testId={testIds.name}
              required
            />
          </FormRow>
          <FormRow>
            <InputField<OrganisationDetailsFormState>
              type="text"
              translationPrefix="stopDetails.maintenance.organisation"
              fieldPath="privateContactDetails.phone"
              testId={testIds.phone}
            />
          </FormRow>
          <FormRow>
            <InputField<OrganisationDetailsFormState>
              type="text"
              translationPrefix="stopDetails.maintenance.organisation"
              fieldPath="privateContactDetails.email"
              testId={testIds.email}
              required
            />
          </FormRow>
          <Visible visible={!!defaultValues.id}>
            <p>
              {t(
                ($) =>
                  $.stopDetails.maintenance.organisation.modalEditDisclaimer,
              )}
            </p>
          </Visible>
        </div>
        <Row className="-mx-12 mt-8 -mb-8 justify-end gap-2 border border-light-grey bg-background px-12 py-2">
          <SimpleButton
            onClick={onCancel}
            inverted
            testId={testIds.cancelButton}
          >
            {t(($) => $.cancel)}
          </SimpleButton>
          <SimpleButton
            onClick={onSave}
            id="save-button"
            testId={testIds.saveButton}
            disabled={
              !methods.formState.isDirty || methods.formState.isSubmitting
            }
          >
            {t(($) => $.save)}
          </SimpleButton>
        </Row>
      </form>
    </FormProvider>
  );
};
